import { GetStaticPaths, GetStaticProps } from "next";
import Image from "next/image";
import { ParsedUrlQuery } from "querystring";
import { useMedia } from "react-use";
import SubPageLayout from "../../components/common/layouts/SubPageLayout";
import LeftArrowSvg from "../../components/common/ui/LeftArrowSvg";
import NormalAnimatedBtn from "../../components/common/ui/NormalAnimatedBtn";
import { MOBILE_MAX_WIDTH } from "../../constant";
import { dateFormat } from "../../services/dateFormat";
import {
  buildNewsListPath,
  buildNewsSrc,
  fetchNewsIds,
  fetchOneNews,
} from "../../services/news";
import styles from "../../styles/News.module.scss";
import { News } from "../../types/newsType";

interface Paths extends ParsedUrlQuery {
  id: string;
}

export const getStaticPaths: GetStaticPaths<Paths> = async () => {
  const data = await fetchNewsIds();
  const contents = data?.contents ?? [];
  const paths = contents.map((e) => ({
    params: { id: e.id },
  }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<News | {}, Paths> = async ({
  params,
}) => {
  const data = await fetchOneNews(params!.id);

  return {
    props: data ?? {},
  };
};

const back = () => {
  if (window.history.state.idx > 0) window.history.back();
  else window.location.href = buildNewsListPath(0);
};

const News = (n: News) => {
  const isMobile = useMedia(`(max-width: ${MOBILE_MAX_WIDTH}px)`, false);

  return (
    <SubPageLayout
      title={`NEWS | ${n.title}`}
      titleImgSrc="/images/text_images/news_vertical.svg"
      bgImgUrl="/images/bg-gallery-pc.jpg"
      spBgImgUrl="/images/bg-gallery-sp.jpg"
      isMobile={isMobile}
      extraContainerClass={
        isMobile ? "mt-[37px]" : "min-h-[calc(100vh-180px)] mt-[36px]"
      }
    >
      <div className="overflow-x-hidden">
        <div className="mb-[16px]">
          <h1
            className={`
              mb-[8px] ${
                isMobile
                  ? "text-[20px] leading-[30px] font-bold"
                  : "text-[44px] leading-[46.2px] font-normal"
              }
            `}
          >
            {n.title}
          </h1>
          <div
            className={`text-right font-bold leading-[16px] ${
              isMobile ? "text-[12px]" : "text-[16px]"
            }`}
          >
            {dateFormat(n.publishedAt)}
          </div>
        </div>
        <div className={styles.imageContainer}>
          <Image
            className={styles.imageContainerInnerImage}
            src={buildNewsSrc(n)}
            alt="eye catch"
            layout="fill"
            objectFit="contain"
          />
        </div>
        <div
          className={styles.newsDetailContent}
          dangerouslySetInnerHTML={{ __html: n.content }}
        />
        <div className={styles["back-button"]}>
          <NormalAnimatedBtn onClick={back} extraClass="py-[12px] px-[27.5px]">
            <div className="w-[16px] h-[16px] flex justify-center items-center mr-[4px]">
              <LeftArrowSvg />
            </div>
            <span>BACK</span>
          </NormalAnimatedBtn>
        </div>
      </div>
    </SubPageLayout>
  );
};

export default News;
